import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToId } from "./utils";
import { Logo } from "./Logo";

const EASE = [0.22, 1, 0.36, 1];

const Line = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yBg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="hero"
      ref={ref}
      className="relative min-h-[100svh] flex items-center pt-32 pb-24 overflow-hidden"
      data-testid="hero-section"
    >
      <motion.div style={{ y: yBg }} className="absolute inset-0 sm-hero-glow pointer-events-none" />
      <motion.div
        style={{ y: yBg }}
        className="absolute inset-0 opacity-[0.06] pointer-events-none"
        aria-hidden
      >
        <div
          className="h-full w-full"
          style={{
            backgroundImage:
              "linear-gradient(rgba(148,163,184,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.5) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage: "radial-gradient(70% 60% at 50% 40%, black, transparent)",
            WebkitMaskImage: "radial-gradient(70% 60% at 50% 40%, black, transparent)",
          }}
        />
      </motion.div>

      <motion.div style={{ opacity }} className="relative mx-auto max-w-[1240px] px-6 md:px-12 w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="inline-flex items-center gap-2.5 rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5 mb-8"
          data-testid="hero-pilot-label"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-teal" />
          </span>
          <span className="text-xs font-medium tracking-wide text-slate-300">Currently in pilot</span>
        </motion.div>

        <div
          className="font-heading font-semibold tracking-tight text-slate-100 text-5xl sm:text-6xl lg:text-7xl leading-[1.08] max-w-5xl mb-7"
          data-testid="hero-hook"
        >
          <Line delay={0.16}>Your AI doesn't know you.</Line>
          <Line delay={0.26}>It only learns from your data.</Line>
          <Line delay={0.36}>
            Build your own <span className="text-teal">personal AI engine</span> instead.
          </Line>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.46, ease: EASE }}
          className="my-16 md:my-24 flex flex-col items-center"
          data-testid="hero-logo"
        >
          <Logo className="h-14 md:h-20 w-auto opacity-90" />
          <div className="mt-8 md:mt-10 h-px w-40 md:w-56 bg-gradient-to-r from-transparent via-white/15 to-transparent" />
        </motion.div>

        <h1 className="font-heading font-semibold tracking-tight text-slate-50 text-4xl sm:text-5xl leading-[1.08] max-w-3xl">
          <Line delay={0.52}>Your personal</Line>
          <Line delay={0.64}>
            <span className="text-teal">Sales IP</span> engine
          </Line>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
          className="mt-8 max-w-2xl text-lg leading-relaxed text-slate-400"
          data-testid="hero-subheadline"
        >
          SalesMoat uses voice-to-text and a conversational coach to capture, structure, and compound
          the high-value context salespeople normally lose — so your AI (and you) work from signal, not noise.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.98, ease: EASE }}
          className="mt-4 max-w-2xl text-sm leading-relaxed text-slate-500"
          data-testid="hero-privacy-line"
        >
          Private by design. Your data stays yours — never used to train shared models.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
          className="mt-11 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <button
            onClick={() => scrollToId("waitlist")}
            data-testid="hero-join-waitlist"
            className="group inline-flex items-center justify-center gap-2 rounded-full bg-teal px-7 py-3.5 text-sm font-semibold text-navy transition-all duration-300 hover:bg-teal-dark hover:shadow-[0_8px_30px_rgba(20,184,166,0.25)]"
          >
            Join the waitlist
          </button>
          <button
            onClick={() => scrollToId("how-it-works")}
            data-testid="hero-how-it-works"
            className="inline-flex items-center justify-center gap-2 rounded-full border border-white/12 px-7 py-3.5 text-sm font-medium text-slate-200 transition-all duration-300 hover:bg-white/[0.05] hover:border-white/20"
          >
            See how it works
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-600"
      >
        <ArrowDown className="h-5 w-5 animate-bounce" />
      </motion.div>
    </section>
  );
};
